import { open, type NitroSQLiteConnection, type QueryResult } from "~/services/expo-sqlite/web-sqlite"

// 统一串行执行，避免并发导致锁等待/卡死（尤其是 logout 清库时）
let globalSqlQueue: Promise<unknown> = Promise.resolve()

export type ColumnMapping<T> = Record<string, unknown> & Partial<Record<keyof T & string, unknown>>
export type IStatement = Record<string, string>

class SqliteDatabaseModel<T> {
  static databaseName = "NBCHAT-SQLITE.sqlite"
  private _tableName: string
  // private _logger: typeof console.log
  private _statements: IStatement
  private _db: NitroSQLiteConnection
  private _tableColumnsPromise?: Promise<Set<string>>

  private static _dbByName = new Map<string, NitroSQLiteConnection>()

  constructor({
    tableName,
    columMapping,
    genStatements,
    logger,
  }: {
    tableName: string
    columMapping: ColumnMapping<T>
    genStatements: (tableName: string) => IStatement
    logger?: typeof console.log
  }) {
    this._tableName = tableName
    void columMapping
    this._statements = genStatements(tableName)
    const dbName = SqliteDatabaseModel.databaseName
    const existing = SqliteDatabaseModel._dbByName.get(dbName)
    this._db = existing ?? open({ name: dbName })
    if (!existing) SqliteDatabaseModel._dbByName.set(dbName, this._db)
    // this._logger = logger || ((...args) => { return args })
  }

  public async run() {
    const statements = Object.values(this._statements)
    for (const sql of statements) {
      await this._execute(sql, [])
    }
  }

  public async reset() {
    await this._execute(`DROP TABLE IF EXISTS ${this._tableName};`, [])
    await this.run()
  }

  public async insert(inserObj: T): Promise<T> {
    // this._logger(`[${this._tableName}]:[insert]`, JSON.stringify(inserObj, null, 2))
    const colsSet = await this._getTableColumns()
    const entries = Object.entries(inserObj as any).filter(
      ([k, v]) => v !== undefined && (colsSet.size === 0 || colsSet.has(k)),
    )
    const cols = entries.map(([k]) => k)
    if (cols.length === 0) throw new Error("insert requires at least one defined field")
    const params = entries.map(([, v]) => this._normalizeParam(v))
    const placeholders = cols.map(() => "?").join(", ")
    const sql = `INSERT INTO ${this._tableName} (${cols.join(", ")}) VALUES (${placeholders});`
    const r = await this._execute(sql, params)
    const insertId = (r as any)?.insertId
    if (insertId === undefined || insertId === null) return inserObj
    const rows = await this.excuteSql(`SELECT * FROM ${this._tableName} WHERE id = ? LIMIT 1;`, [
      insertId,
    ])
    return (rows?.[0] ?? inserObj) as T
  }

  public async update(updateObj: T): Promise<T> {
    // this._logger(`[${this._tableName}]:[update]`, JSON.stringify(updateObj, null, 2))
    const id = (updateObj as any)?.id
    if (id === undefined || id === null) throw new Error("update requires updateObj.id")
    const colsSet = await this._getTableColumns()
    const entries = Object.entries(updateObj as any).filter(
      ([k, v]) => k !== "id" && v !== undefined && (colsSet.size === 0 || colsSet.has(k)),
    )
    if (entries.length === 0) return updateObj
    const setSql = entries.map(([k]) => `${k} = ?`).join(", ")
    const params = entries.map(([, v]) => this._normalizeParam(v))
    await this._execute(`UPDATE ${this._tableName} SET ${setSql} WHERE id = ?;`, [
      ...params,
      this._normalizeParam(id),
    ])
    const rows = await this.excuteSql(`SELECT * FROM ${this._tableName} WHERE id = ? LIMIT 1;`, [
      id,
    ])
    return (rows?.[0] ?? updateObj) as T
  }

  public async destroy(id): Promise<boolean> {
    // this._logger(`[${this._tableName}]:[destroy]`, id)
    const r = await this._execute(`DELETE FROM ${this._tableName} WHERE id = ?;`, [
      this._normalizeParam(id),
    ])
    return ((r as any)?.rowsAffected ?? 0) > 0
  }

  public async destroyAll(): Promise<boolean> {
    // this._logger(`[${this._tableName}]:[destroyAll]`)
    // 不再走 orm 的 destroyAll（内部可能并发/锁等待不可控）
    // 统一走 excuteSql 的串行队列与超时保护
    try {
      await this.excuteSql(`DELETE FROM ${this._tableName};`, [])
      return true
    } catch {
      return false
    }
  }

  public async find(id): Promise<T> {
    // this._logger(`[${this._tableName}]:[find]`, id)
    const rows = await this.excuteSql(`SELECT * FROM ${this._tableName} WHERE id = ? LIMIT 1;`, [
      id,
    ])
    return (rows?.[0] ?? null) as any
  }

  public async findBy(where): Promise<T> {
    // this._logger(`[${this._tableName}]:[findBy]`, JSON.stringify(where, null, 2))
    const entries = Object.entries(where ?? {}).filter(([, v]) => v !== undefined)
    if (entries.length === 0) return null as any
    const whereSql = entries.map(([k]) => `${k} = ?`).join(" AND ")
    const params = entries.map(([, v]) => this._normalizeParam(v))
    const rows = await this.excuteSql(
      `SELECT * FROM ${this._tableName} WHERE ${whereSql} LIMIT 1;`,
      params,
    )
    return (rows?.[0] ?? null) as any
  }

  public async query(options): Promise<T[]> {
    // this._logger(`[${this._tableName}]:[query]`, JSON.stringify(options, null, 2))
    const where = options?.where ?? null
    const order = options?.order ?? null
    const limit = options?.limit ?? null
    const offset = options?.offset ?? null

    const params: any[] = []
    let sql = `SELECT * FROM ${this._tableName}`

    if (where && typeof where === "object") {
      const entries = Object.entries(where).filter(([, v]) => v !== undefined)
      if (entries.length > 0) {
        sql += " WHERE " + entries.map(([k]) => `${k} = ?`).join(" AND ")
        params.push(...entries.map(([, v]) => this._normalizeParam(v)))
      }
    }
    if (order && typeof order === "string") sql += ` ORDER BY ${order}`
    if (typeof limit === "number") sql += ` LIMIT ${limit}`
    if (typeof offset === "number") sql += ` OFFSET ${offset}`
    sql += ";"

    return (await this.excuteSql(sql, params)) as any
  }

  public async excuteSql(sql: string, params: any[]): Promise<T[]> {
    // this._logger(`[${this._tableName}]:[excuteSql]`, sql, params)
    return new Promise((resolve, reject) => {
      globalSqlQueue = globalSqlQueue.then(async () => {
        try {
          const normalized = (params ?? []).map((p) => this._normalizeParam(p))
          const r = await this._execute(sql, normalized)
          resolve(((r as any)?.results ?? []) as any)
        } catch (e) {
          reject(e)
        }
      })
    })
  }

  private async _execute(sql: string, params: any[], timeoutMs = 10000): Promise<QueryResult> {
    const timeout = new Promise<never>((_, rj) =>
      setTimeout(() => rj(new Error("SQL timeout")), timeoutMs),
    )
    const task = this._db.executeAsync(sql, params)
    return (await Promise.race([task, timeout])) as QueryResult
  }

  private async _getTableColumns(): Promise<Set<string>> {
    if (!this._tableColumnsPromise) {
      this._tableColumnsPromise = (async () => {
        try {
          const r = await this._execute(`PRAGMA table_info(${this._tableName});`, [], 5000)
          const cols = new Set<string>()
          for (const row of (r as any)?.results ?? []) {
            const name = row?.name
            if (typeof name === "string" && name.length > 0) cols.add(name)
          }
          return cols
        } catch {
          return new Set<string>()
        }
      })()
    }
    return this._tableColumnsPromise
  }

  private _normalizeParam(v: any) {
    if (v === undefined) return null
    if (v === null) return null
    if (typeof v === "string") return v
    if (typeof v === "number") return v
    if (typeof v === "boolean") return v
    if (typeof v === "bigint") return Number(v)
    // expo-sqlite-orm 的 columnTypes.JSON 之前会自动处理对象/数组
    if (typeof v === "object") return JSON.stringify(v)
    return String(v)
  }
}

export default SqliteDatabaseModel
